// src/app/undo-redo/undo-redo.service.ts

import { Injectable, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UndoRedoOperation } from './undo-redo-operation.interface';

// En undo-redo.service.ts
const MAX_HISTORY_SIZE = 50; // número máximo de operaciones a apilar para Undo/Redo

@Injectable({
  providedIn: 'root'
})
export class UndoRedoService implements OnDestroy {
  // Stacks to store the operations
  private undoStack: UndoRedoOperation[] = [];
  private redoStack: UndoRedoOperation[] = [];

  // Observables to notify components about the availability of actions
  private _canUndo$ = new BehaviorSubject<boolean>(false);
  private _canRedo$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable that indicates if the Undo operation is currently available.
   */
  public readonly canUndo$: Observable<boolean> = this._canUndo$.asObservable();

  /**
   * Observable that indicates if the Redo operation is currently available.
   */
  public readonly canRedo$: Observable<boolean> = this._canRedo$.asObservable();

  private keyboardSubscription: Subscription;
  // Acceder a window a través de DOCUMENT y defaultView
  private readonly window: Window;

  constructor(@Inject(DOCUMENT) private document: Document) {
    // Obtener la referencia segura a window
    this.window = this.document.defaultView as Window;
    // 1. Usar RxJS 'fromEvent' para escuchar el evento
    this.keyboardSubscription = fromEvent<KeyboardEvent>(this.window, 'keydown').pipe(
      // 2. Filtrar solo los eventos que nos interesan (Ctrl+Z o Ctrl+Y)
      filter(event => (event.ctrlKey || event.metaKey) && (event.key === 'z' || event.key === 'y'))
    ).subscribe(event => {
      // 3. Procesar el evento (la lógica de tu servicio)
      this.handleKeyboardEvent(event);
    });

    this.updateObservables();
  }

  // --- Keyboard Shortcuts (Default: Ctrl+Z and Ctrl+Y) ---

  /**
   * @param event The keyboard event.
   */
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Check for Ctrl/Cmd
    const isCtrlCmd = event.ctrlKey || event.metaKey;

    if (isCtrlCmd && event.key === 'z') {
      // Ctrl + Z (or Cmd + Z) -> Undo
      event.preventDefault(); // Prevents browser default behavior
      this.undo();
    } else if (isCtrlCmd && event.key === 'y') {
      // Ctrl + Y (or Cmd + Y) -> Redo
      event.preventDefault();
      this.redo();
    }
  }

  // Implementar OnDestroy para limpiar la suscripción
  ngOnDestroy(): void {
    if (this.keyboardSubscription) {
      this.keyboardSubscription.unsubscribe();
    }
    // Nota: aunque el servicio es 'providedIn: root', que solo se destruye
    // al cerrar la aplicación, usar 'unsubscribe' es la mejor práctica.
  }

  /**
   * Adds a new operation to the undo stack.
   * Clears the redo stack when a new operation is added.
   * @param operation The operation object implementing the Operation interface.
   */
  addOperation(operation: UndoRedoOperation): void {
    // 1. Añade la nueva operación
    this.undoStack.push(operation);

    // 2. Limpia la pila de Rehacer, ya que la nueva operación la invalida
    this.redoStack = [];

    // 3. Aplica el límite de pila de operaciones si se excede
    if (this.undoStack.length > MAX_HISTORY_SIZE) {
      // Usar slice para mantener solo los últimos N elementos
      // Se eliminan los elementos más antiguos (al inicio del array)
      this.undoStack = this.undoStack.slice(this.undoStack.length - MAX_HISTORY_SIZE);
    }

    this.updateObservables();
  }

  /**
   * Performs the 'Undo' action.
   */
  undo(): void {
    if (this.canUndo()) {
      const operationToUndo = this.undoStack.pop();
      if (operationToUndo) {
        operationToUndo.undo(); // Execute the undo logic
        this.redoStack.push(operationToUndo); // Move to the redo stack
        this.updateObservables();
      }
    }
  }

  /**
   * Performs the 'Redo' action.
   */
  redo(): void {
    if (this.canRedo()) {
      const operationToRedo = this.redoStack.pop();
      if (operationToRedo) {
        operationToRedo.redo(); // Execute the redo logic
        this.undoStack.push(operationToRedo); // Move back to the undo stack
        this.updateObservables();
      }
    }
  }

  /**
   * Checks if an Undo operation is possible.
   * @returns true if the undo stack is not empty.
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Checks if a Redo operation is possible.
   * @returns true if the redo stack is not empty.
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Resets the service, clearing both stacks.
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.updateObservables();
  }

  // --- Internal Helper ---
  private updateObservables(): void {
    this._canUndo$.next(this.canUndo());
    this._canRedo$.next(this.canRedo());
  }
}
